<template>
    <div>

        <div v-if="! loaded && loading" class="rv-loading">Loading reviews...</div>

        <template v-else-if="loaded">

            <template v-if="summary.count > 0">

                <!-- Сводка: оценка + распределение (бары кликабельны = фильтр) -->
                <div class="rv-summary">
                    <div class="rv-score">
                        <div class="rv-score-num">{{ summary.rating.toFixed(1) }}</div>
                        <div class="rv-score-stars">★★★★★</div>
                        <div class="rv-score-count">{{ summary.count }} reviews</div>
                    </div>
                    <div class="rv-bars">
                        <button
                            v-for="s in [5, 4, 3, 2, 1]"
                            :key="s"
                            type="button"
                            class="rv-bar"
                            :class="{ active: star === s }"
                            :title="'Show ' + s + '-star reviews'"
                            @click="filterStar( s )"
                        >
                            <span class="rv-bar-label">{{ s }}★</span>
                            <span class="rv-bar-track"><span class="rv-bar-fill" :style="{ width: barWidth( s ) }"></span></span>
                            <span class="rv-bar-count">{{ summary.dist[ s ] || 0 }}</span>
                        </button>
                    </div>
                </div>

                <!-- Фильтры -->
                <div class="rv-toolbar">
                    <button
                        type="button"
                        class="rv-chip"
                        :class="{ active: star === 0 && ! withImages }"
                        @click="filterAll"
                    >All</button>
                    <button
                        type="button"
                        class="rv-chip"
                        :class="{ active: withImages }"
                        @click="filterImages"
                    >📷 With photos</button>
                </div>

                <!-- Список -->
                <div class="rv-list">
                    <div v-for="r in items" :key="r.id" class="rv-card">
                        <div class="rv-head">
                            <span class="rv-author">{{ r.author_name }}</span>
                            <span class="rv-stars">{{ '★'.repeat( r.star ) }}</span>
                            <span class="rv-meta">{{ r.flag }} · {{ shortDate( r.created_at ) }}</span>
                        </div>
                        <div class="rv-text">{{ r.content }}</div>
                        <div v-if="r.images.length" class="rv-photos">
                            <button
                                v-for="( img, i ) in r.images"
                                :key="i"
                                type="button"
                                class="rv-photo-btn"
                                @click="openLightbox( r.images, i )"
                            >
                                <img :src="img" loading="lazy" alt="Customer photo">
                            </button>
                        </div>
                        <div class="rv-actions">
                            <button
                                type="button"
                                class="rv-like"
                                :class="{ active: r.vote === 1 }"
                                @click="like( r, true )"
                            >👍 Helpful <span class="rv-like-n">{{ r.like || '' }}</span></button>
                            <button
                                type="button"
                                class="rv-like"
                                :class="{ active: r.vote === 0 }"
                                @click="like( r, false )"
                            >👎 <span class="rv-like-n">{{ r.unlike || '' }}</span></button>
                        </div>
                    </div>

                    <p v-if="items.length === 0 && ! loading" class="rv-empty">No reviews match this filter.</p>
                </div>

                <button
                    v-if="page < pages"
                    type="button"
                    class="rv-more"
                    :disabled="loading"
                    @click="loadMore"
                >{{ loading ? 'Loading...' : 'Load more reviews' }}</button>

            </template>

            <p v-else class="rv-empty">No reviews yet — be the first to try it.</p>

        </template>

        <!-- Лайтбокс фото отзыва (29.07): клик по превью - модалка
             с галереей, стрелки/Esc, клик по фону закрывает -->
        <div v-if="lightbox.open" class="rv-lightbox" @click.self="closeLightbox">
            <button type="button" class="rv-lb-close" aria-label="Close" @click="closeLightbox">✕</button>
            <button
                v-if="lightbox.images.length > 1"
                type="button"
                class="btn-arrow btn-arrow--dark rv-lb-arrow rv-lb-arrow--l"
                aria-label="Previous photo"
                @click.stop="lightboxStep( -1 )"
            >‹</button>
            <img class="rv-lb-img" :src="lightbox.images[ lightbox.index ]" alt="Customer photo">
            <button
                v-if="lightbox.images.length > 1"
                type="button"
                class="btn-arrow btn-arrow--dark rv-lb-arrow rv-lb-arrow--r"
                aria-label="Next photo"
                @click.stop="lightboxStep( 1 )"
            >›</button>
            <div v-if="lightbox.images.length > 1" class="rv-lb-count">
                {{ lightbox.index + 1 }} / {{ lightbox.images.length }}
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Отзывы сингла - Vue-остров (29.07, вердикт Витали): НИКАКОГО
 * серверного рендера отзывов, все данные ajax review/list (summary
 * приезжает тем же запросом). Ленивая загрузка: первый fetch - при
 * открытии таба Reviews (reviews.js дёргает load()).
 *
 * Лайки: review/like, гость мягко уводится на /login.
 */
export default {
    name: 'ReviewsApp',

    props: {
        productId : { type: Number, required: true },
        pageSize  : { type: Number, default: 10 },
    },

    data() {
        return {
            loaded     : false,
            loading    : false,
            items      : [],
            page       : 1,
            pages      : 0,
            star       : 0,
            withImages : false,
            summary    : { count: 0, rating: 0, dist: {} },
            lightbox   : { open: false, images: [], index: 0 },
        };
    },

    mounted() {
        // клавиатура лайтбокса: Esc закрыть, стрелки листать
        this._onKey = ( e ) => {

            if( ! this.lightbox.open )
                return;

            if( e.key === 'Escape' )
                this.closeLightbox();
            if( e.key === 'ArrowLeft' )
                this.lightboxStep( -1 );
            if( e.key === 'ArrowRight' )
                this.lightboxStep( 1 );
        };
        document.addEventListener( 'keydown', this._onKey );
    },

    unmounted() {
        document.removeEventListener( 'keydown', this._onKey );
        document.body.style.overflow = '';
    },

    methods: {

        openLightbox( images, index ) {

            this.lightbox = { open: true, images: images, index: index };

            document.body.style.overflow = 'hidden';   // страница не скроллится под модалкой
        },

        closeLightbox() {

            this.lightbox.open = false;

            document.body.style.overflow = '';
        },

        lightboxStep( dir ) {

            const n = this.lightbox.images.length;

            this.lightbox.index = ( this.lightbox.index + dir + n ) % n;
        },

        // первый заход (зовёт reviews.js при открытии таба)
        load() {

            if( this.loaded || this.loading )
                return;

            this.fetchPage( true );
        },

        async fetchPage( reset ) {

            this.loading = true;

            try {
                const response = await this.$http.post( '/review/list', {
                    product_id : this.productId,
                    page       : this.page,
                    page_size  : this.pageSize,
                    star       : this.star,
                    has_images : this.withImages,
                } );
                const { status, data } = response?.data || {};

                if( status === 'success' ) {
                    this.items   = reset ? ( data.items || [] ) : this.items.concat( data.items || [] );
                    this.pages   = data.pages || 0;
                    this.summary = data.summary || this.summary;
                    this.loaded  = true;
                }
            } catch( e ) {
                // отзывы не должны ронять сингл - тихий фейл, таб покажет пустоту
            } finally {
                this.loading = false;
            }
        },

        loadMore() {
            this.page++;
            this.fetchPage( false );
        },

        filterStar( s ) {

            // повторный клик по тому же бару = сброс фильтра
            this.star       = this.star === s ? 0 : s;
            this.withImages = false;

            this.refetch();
        },

        filterAll() {

            this.star       = 0;
            this.withImages = false;

            this.refetch();
        },

        filterImages() {

            this.star       = 0;
            this.withImages = true;

            this.refetch();
        },

        refetch() {
            this.page = 1;
            this.fetchPage( true );
        },

        async like( review, isLike ) {

            try {
                const response = await this.$http.post('/review/like', {
                    id: review.id,
                    like: isLike,
                });
                const {status, data} = response?.data || {};

                if (status !== 'success')
                    return;

                // гостя НЕ уводим со страницы (вердикт 29.07) - только
                // уведомляем: лайки для авторизованных
                if (data.notAuth) {
                    this.$alert.info(data.message || 'Only signed-in customers can rate reviews');
                    return;
                }

                if (data.stats) {
                    review.like = data.stats.like;
                    review.unlike = data.stats.unlike;
                    review.vote = data.vote >= 0 ? data.vote : null;
                }
            } catch (e) {
            }
        },

        barWidth(s) {

            if (!this.summary.count)
                return '0%';

            return Math.floor((this.summary.dist[s] || 0) * 100 / this.summary.count) + '%';
        },

        shortDate(dt) {
            return (dt || '').slice(0, 10);
        },
    },
};
</script>